// ###### IMPORTS
import React, { useState, useEffect } from 'react';
import { XMarkIcon, PencilSquareIcon } from '@heroicons/react/24/solid';
import { toast } from 'react-hot-toast';
import { updateAssetOperation, deleteAssetOperation } from '../../viewServices/assets_api';
import SpinnerLoad from 'views/finance/moduleComponents/SpinnerLoad';
import ModalConfirm from 'views/finance/moduleComponents/ModalConfirm';
import DatePicker from 'shared_items/DatePicker';
import { toDatePart } from 'shared_items/formatConventions';

// ###### HELPERS
const parseDecimal = (v) => {
  if (v === '' || v == null) return null;
  const n = parseFloat(String(v).replace(',', '.'));
  return Number.isNaN(n) ? null : n;
};

// ###### COMPONENT
const ModalOperationEdit = ({ selectedOperation, onClose, onSuccess }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editedOperation, setEditedOperation] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (selectedOperation) {
      setEditedOperation({
        operation_status: selectedOperation.operation_status || 'buy',
        // Date-only fields ("YYYY-MM-DD"): keep as string, never parse via new Date() (UTC shift)
        operation_date: toDatePart(selectedOperation.operation_date),
        redeem_date: toDatePart(selectedOperation.redeem_date),
        quantity: selectedOperation.quantity != null ? String(selectedOperation.quantity) : '',
        total_value: selectedOperation.total_value != null ? String(selectedOperation.total_value) : '',
        net_value: selectedOperation.net_value != null ? String(selectedOperation.net_value) : '',
        rate_description: selectedOperation.rate_description || '',
        notes: selectedOperation.notes || '',
      });
      setIsEditing(false);
    }
  }, [selectedOperation]);

  if (!selectedOperation || !editedOperation) return null;

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setEditedOperation((prev) => ({ ...prev, [name]: value }));
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    if (!editedOperation.operation_date?.trim()) {
      toast.error('Data da operação é obrigatória.');
      return;
    }
    const totalVal = parseDecimal(editedOperation.total_value);
    if (totalVal === null) {
      toast.error('Valor total é obrigatório e deve ser um número válido.');
      return;
    }
    const netVal = parseDecimal(editedOperation.net_value);
    if (editedOperation.net_value?.trim() !== '' && netVal === null) {
      toast.error('Valor líquido inválido.');
      return;
    }
    const qty = parseDecimal(editedOperation.quantity);
    if (editedOperation.quantity?.trim() !== '' && qty === null) {
      toast.error('Quantidade inválida.');
      return;
    }
    try {
      setSubmitting(true);
      const payload = {
        operation_status: editedOperation.operation_status,
        operation_date: editedOperation.operation_date,
        redeem_date: editedOperation.redeem_date?.trim() || null,
        quantity: qty,
        total_value: totalVal != null ? totalVal : parseFloat(selectedOperation.total_value),
        net_value: netVal != null ? netVal : (parseFloat(selectedOperation.net_value) ?? totalVal),
        rate_description: editedOperation.rate_description?.trim() || null,
        notes: editedOperation.notes?.trim() || null,
      };
      await updateAssetOperation(selectedOperation.id, payload);
      toast.success('Operação atualizada.');
      onSuccess?.();
      onClose?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || 'Erro ao atualizar operação.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteOperation = async () => {
    if (!selectedOperation?.id) return;
    setShowDeleteConfirm(false);
    try {
      setDeleting(true);
      await deleteAssetOperation(selectedOperation.id);
      toast.success('Operação excluída.');
      onSuccess?.();
      onClose?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || 'Erro ao excluir operação.');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <>
    <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
      <div className="bg-white p-4 md:p-6 rounded-2xl shadow w-80 md:w-96 relative max-h-[90vh] overflow-y-auto">
        {(submitting || deleting) && (
          <div className="absolute inset-0 flex items-center justify-center z-10 bg-white/90 rounded-2xl">
            <div className="bg-white rounded-xl shadow-lg p-4 border border-gray-200">
              <SpinnerLoad />
            </div>
          </div>
        )}
        <div className="absolute top-2 right-2 flex gap-2">
          {!isEditing && (
            <button
              type="button"
              onClick={() => setIsEditing(true)}
              disabled={submitting || deleting}
              className={`text-gray-500 ${submitting || deleting ? 'opacity-50 cursor-not-allowed' : 'hover:text-gray-700'}`}
              title="Editar"
            >
              <PencilSquareIcon className="h-5 w-5" />
            </button>
          )}
          <button
            type="button"
            onClick={onClose}
            disabled={submitting || deleting}
            className={`text-gray-500 ${submitting || deleting ? 'opacity-50 cursor-not-allowed' : 'hover:text-gray-700'}`}
            title="Fechar"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        <h2 className="text-lg font-semibold mb-4 pr-10">
          {isEditing ? 'Editar operação' : 'Detalhes da operação'}
        </h2>

        <form className="space-y-4" onSubmit={handleUpdate}>
          <div>
            <label className="block text-xs text-gray-500 mb-0.5">Asset</label>
            <div className="px-2 py-1.5 border rounded-md text-sm bg-gray-50 text-gray-600">
              <span className="font-medium text-gray-800">{selectedOperation.asset_name ?? '—'}</span>
              <span className="ml-2 text-xs font-mono text-gray-500">{selectedOperation.asset_code ?? '—'}</span>
            </div>
          </div>

          <div>
            <label className="block text-xs text-gray-500 mb-1">Tipo</label>
            <div className="bg-gray-100 rounded-full p-0.5 inline-flex">
              {[
                { value: 'buy', label: 'Buy' },
                { value: 'sell', label: 'Sell' },
              ].map(({ value, label }) => (
                <button
                  key={value}
                  type="button"
                  disabled={!isEditing || submitting || deleting}
                  onClick={() => setEditedOperation((prev) => ({
                    ...prev,
                    operation_status: value,
                    ...(value === 'sell' ? { redeem_date: '' } : {}),
                  }))}
                  className={`px-3 py-1 text-xs font-medium transition-all ${
                    value === 'buy' ? 'rounded-l-full' : 'rounded-r-full'
                  } ${
                    editedOperation.operation_status === value
                      ? value === 'buy'
                        ? 'bg-emerald-100 text-emerald-800'
                        : 'bg-rose-100 text-rose-800'
                      : 'text-gray-700 hover:bg-gray-200'
                  } ${(!isEditing || submitting || deleting) ? 'opacity-60 cursor-not-allowed' : ''}`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          <div className="flex gap-2">
            <div className="w-1/2 min-w-0 shrink-0">
              <label className="block text-xs text-gray-500 mb-0.5">Data da operação</label>
              <DatePicker
                name="operation_date"
                value={editedOperation.operation_date}
                onChange={handleInputChange}
                mode="single"
                colorScheme="green"
                disabled={!isEditing || submitting || deleting}
                required
                placeholder="Select date"
                className="w-full justify-between min-h-[2.25rem]"
              />
            </div>
            {editedOperation.operation_status === 'buy' ? (
              <div className="w-1/2 min-w-0 shrink-0">
                <label className="block text-xs text-gray-500 mb-0.5">Redeem date (opcional)</label>
                <DatePicker
                  name="redeem_date"
                  value={editedOperation.redeem_date}
                  onChange={handleInputChange}
                  mode="single"
                  colorScheme="green"
                  disabled={!isEditing || submitting || deleting}
                  placeholder="Select date"
                  className="w-full justify-between min-h-[2.25rem]"
                />
              </div>
            ) : (
              <div className="w-1/2 min-w-0 shrink-0" aria-hidden />
            )}
          </div>

          <div>
            <label className="block text-xs text-gray-500 mb-0.5">Quantidade (opcional)</label>
            <input
              type="text"
              name="quantity"
              value={editedOperation.quantity}
              onChange={handleInputChange}
              placeholder="Ex: 100"
              className="w-full px-2 py-1.5 border rounded-md text-sm"
              disabled={!isEditing || submitting || deleting}
              inputMode="decimal"
            />
          </div>

          <div className="flex gap-2">
            <div className="flex-1 min-w-0">
              <label className="block text-xs text-gray-500 mb-0.5">Valor total (R$)</label>
              <input
                type="text"
                name="total_value"
                value={editedOperation.total_value}
                onChange={handleInputChange}
                placeholder="0,00"
                className="w-full px-2 py-1.5 border rounded-md text-sm tabular-nums"
                disabled={!isEditing || submitting || deleting}
                inputMode="decimal"
                required
              />
            </div>
            <div className="flex-1 min-w-0">
              <label className="block text-xs text-gray-500 mb-0.5">Valor líquido (R$)</label>
              <input
                type="text"
                name="net_value"
                value={editedOperation.net_value}
                onChange={handleInputChange}
                placeholder="0,00"
                className="w-full px-2 py-1.5 border rounded-md text-sm tabular-nums"
                disabled={!isEditing || submitting || deleting}
                inputMode="decimal"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs text-gray-500 mb-0.5">Descrição da taxa (opcional)</label>
            <input
              type="text"
              name="rate_description"
              value={editedOperation.rate_description}
              onChange={handleInputChange}
              placeholder="Ex: taxa corretora"
              className="w-full px-2 py-1.5 border rounded-md text-sm"
              disabled={!isEditing || submitting || deleting}
            />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-0.5">Observações (opcional)</label>
            <textarea
              name="notes"
              value={editedOperation.notes}
              onChange={handleInputChange}
              placeholder="Notas"
              rows={2}
              className="w-full px-2 py-1.5 border rounded-md text-sm resize-none"
              disabled={!isEditing || submitting || deleting}
            />
          </div>
          {isEditing && (
            <div className="flex flex-wrap items-center gap-2 border-t border-gray-100 pt-3">
              <button
                type="button"
                onClick={() => setShowDeleteConfirm(true)}
                disabled={submitting || deleting}
                className="shrink-0 rounded-xl border border-red-200 px-3 py-2 text-sm font-medium text-red-700 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-50"
              >
                Excluir operação
              </button>
              <div className="ml-auto flex flex-1 flex-wrap justify-end gap-2 min-w-0">
                <button
                  type="button"
                  onClick={onClose}
                  disabled={submitting || deleting}
                  className="px-4 h-10 rounded-2xl border border-gray-300 text-gray-700 hover:bg-gray-100 disabled:opacity-50"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={submitting || deleting}
                  className={`min-w-[7rem] h-10 rounded-2xl px-4 text-white ${submitting || deleting ? 'bg-green-400 cursor-not-allowed' : 'bg-green-600 hover:bg-green-700'}`}
                >
                  {submitting ? 'Salvando...' : 'Salvar'}
                </button>
              </div>
            </div>
          )}
        </form>
      </div>
    </div>

    <ModalConfirm
      isOpen={showDeleteConfirm}
      onClose={() => setShowDeleteConfirm(false)}
      onConfirm={handleDeleteOperation}
      title="Excluir operação"
      message="Esta operação será removida permanentemente. Não é possível desfazer."
      confirmText="Excluir"
      cancelText="Cancelar"
      disabled={deleting}
      confirmButtonClass="bg-red-600 hover:bg-red-700 text-white shadow"
    />
    </>
  );
};

export default ModalOperationEdit;
